// Advanced ROI Calculator - Multi-mode with Premium Design
import React, { useState, useMemo } from 'react';
import { ShoppingCart, Brain, Settings, TrendingUp, Calculator, ArrowRight, Sparkles } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

type CalculatorMode = 'ecommerce' | 'automation' | 'software';

interface ModeConfig {
  id: CalculatorMode;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const modes: ModeConfig[] = [
  {
    id: 'ecommerce',
    label: 'E-commerce',
    icon: <ShoppingCart className="w-5 h-5" />,
    description: 'Optimize conversions',
  },
  {
    id: 'automation',
    label: 'AI Automation',
    icon: <Brain className="w-5 h-5" />,
    description: 'Reduce manual work',
  },
  {
    id: 'software',
    label: 'Custom Software',
    icon: <Settings className="w-5 h-5" />,
    description: 'Replace legacy systems',
  },
];

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatNumber = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`;
  }
  return value.toFixed(0);
};

// Slider Component
const Slider = ({ 
  label, 
  value, 
  min, 
  max, 
  step = 1,
  unit = '',
  prefix = '',
  onChange,
  formatValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  prefix?: string;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
}) => {
  const percentage = ((value - min) / (max - min)) * 100;
  const displayValue = formatValue ? formatValue(value) : `${prefix}${value.toLocaleString()}${unit}`;
  
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-white/70">{label}</label>
        <span className="text-sm font-mono font-semibold text-white">{displayValue}</span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-white/10"
          style={{
            background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${percentage}%, rgba(255,255,255,0.1) ${percentage}%, rgba(255,255,255,0.1) 100%)`,
          }}
        />
      </div>
      <div className="flex justify-between text-xs text-white/30">
        <span>{prefix}{min.toLocaleString()}{unit}</span>
        <span>{prefix}{max.toLocaleString()}{unit}</span>
      </div>
    </div>
  );
};

// Value Display Component with transition effect
const ValueDisplay = ({ value, className = '' }: { value: string; className?: string }) => (
  <span 
    className={`transition-all duration-300 ${className}`}
    style={{ fontVariantNumeric: 'tabular-nums' }}
  >
    {value}
  </span>
);

// E-commerce Calculator
const EcommerceCalculator = () => {
  const [visitors, setVisitors] = useState(50000);
  const [conversionRate, setConversionRate] = useState(2.5);
  const [aov, setAov] = useState(120);
  
  const calculations = useMemo(() => {
    const currentOrders = visitors * (conversionRate / 100);
    const currentRevenue = currentOrders * aov * 12;
    
    const improvements = [1, 2, 3].map(increase => {
      const newCR = conversionRate + increase;
      const newOrders = visitors * (newCR / 100);
      const newRevenue = newOrders * aov * 12;
      return {
        increase,
        newCR,
        revenue: newRevenue,
        gain: newRevenue - currentRevenue,
      };
    });
    
    return { currentRevenue, improvements };
  }, [visitors, conversionRate, aov]);
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Inputs */}
      <div className="space-y-6">
        <Slider
          label="Monthly Visitors"
          value={visitors}
          min={1000}
          max={500000}
          step={1000}
          onChange={setVisitors}
          formatValue={(v) => formatNumber(v)}
        />
        <Slider
          label="Current Conversion Rate"
          value={conversionRate}
          min={0.5}
          max={10}
          step={0.1}
          unit="%"
          onChange={setConversionRate}
        />
        <Slider
          label="Average Order Value"
          value={aov}
          min={20}
          max={500}
          step={5}
          prefix="$"
          onChange={setAov}
        />
      </div>
      
      {/* Results */}
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Current Annual Revenue</p>
          <p className="text-2xl font-bold text-white">
            <ValueDisplay value={formatCurrency(calculations.currentRevenue)} />
          </p>
        </div>
        
        <div className="space-y-3">
          {calculations.improvements.map((imp, i) => (
            <div 
              key={imp.increase}
              className={`p-4 rounded-xl border transition-all duration-300 ${
                i === 2 
                  ? 'bg-primary/10 border-primary/30' 
                  : 'bg-white/[0.02] border-white/[0.06]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/40 mb-1">+{imp.increase}% Conversion Rate</p>
                  <p className="text-xl font-bold text-white">
                    <ValueDisplay value={formatCurrency(imp.revenue)} />
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-primary mb-1">Annual Gain</p>
                  <p className="text-lg font-semibold text-primary">
                    +<ValueDisplay value={formatCurrency(imp.gain)} />
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// AI Automation Calculator
const AutomationCalculator = () => {
  const [hoursPerWeek, setHoursPerWeek] = useState(40);
  const [hourlyRate, setHourlyRate] = useState(50);
  const [employees, setEmployees] = useState(5);
  
  const calculations = useMemo(() => {
    const annualManualCost = hoursPerWeek * hourlyRate * employees * 52;
    const automationSavings = [50, 70, 85].map(percent => {
      const savedHours = hoursPerWeek * (percent / 100) * employees * 52;
      const savedCost = savedHours * hourlyRate;
      return { percent, savedHours, savedCost };
    });
    
    return { annualManualCost, automationSavings };
  }, [hoursPerWeek, hourlyRate, employees]);
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Inputs */}
      <div className="space-y-6">
        <Slider
          label="Manual Hours per Week (per person)"
          value={hoursPerWeek}
          min={5}
          max={60}
          step={1}
          unit=" hrs"
          onChange={setHoursPerWeek}
        />
        <Slider
          label="Average Hourly Cost"
          value={hourlyRate}
          min={20}
          max={200}
          step={5}
          prefix="$"
          onChange={setHourlyRate}
        />
        <Slider
          label="Employees Affected"
          value={employees}
          min={1}
          max={50}
          step={1}
          onChange={setEmployees}
        />
      </div>
      
      {/* Results */}
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Current Annual Manual Cost</p>
          <p className="text-2xl font-bold text-white">
            <ValueDisplay value={formatCurrency(calculations.annualManualCost)} />
          </p>
        </div>
        
        <div className="space-y-3">
          {calculations.automationSavings.map((saving, i) => (
            <div 
              key={saving.percent}
              className={`p-4 rounded-xl border transition-all duration-300 ${
                i === 2 
                  ? 'bg-primary/10 border-primary/30' 
                  : 'bg-white/[0.02] border-white/[0.06]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/40 mb-1">{saving.percent}% Automation</p>
                  <p className="text-lg font-semibold text-white">
                    {formatNumber(saving.savedHours)} hours saved
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-primary mb-1">Annual Savings</p>
                  <p className="text-xl font-bold text-primary">
                    <ValueDisplay value={formatCurrency(saving.savedCost)} />
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Custom Software Calculator
const SoftwareCalculator = () => {
  const [legacyCost, setLegacyCost] = useState(100000);
  const [manualProcesses, setManualProcesses] = useState(8);
  const [integrations, setIntegrations] = useState(4);
  
  const calculations = useMemo(() => {
    const processEfficiency = manualProcesses * 15000; // $15k savings per automated process
    const integrationValue = integrations * 25000; // $25k value per integration
    const maintenanceReduction = legacyCost * 0.4; // 40% reduction in maintenance
    const totalFirstYearValue = processEfficiency + integrationValue + maintenanceReduction;
    const threeYearValue = totalFirstYearValue * 2.5; // Compounding benefits
    
    return { processEfficiency, integrationValue, maintenanceReduction, totalFirstYearValue, threeYearValue };
  }, [legacyCost, manualProcesses, integrations]);
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Inputs */}
      <div className="space-y-6">
        <Slider
          label="Current Annual System Costs"
          value={legacyCost}
          min={10000}
          max={500000}
          step={5000}
          prefix="$"
          onChange={setLegacyCost}
          formatValue={(v) => formatCurrency(v)}
        />
        <Slider
          label="Manual Processes to Automate"
          value={manualProcesses}
          min={1}
          max={20}
          step={1}
          onChange={setManualProcesses}
        />
        <Slider
          label="System Integrations Needed"
          value={integrations}
          min={1}
          max={15}
          step={1}
          onChange={setIntegrations}
        />
      </div>
      
      {/* Results */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Process Savings</p>
            <p className="text-xl font-bold text-white">
              <ValueDisplay value={formatCurrency(calculations.processEfficiency)} />
            </p>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Integration Value</p>
            <p className="text-xl font-bold text-white">
              <ValueDisplay value={formatCurrency(calculations.integrationValue)} />
            </p>
          </div>
        </div>
        
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Maintenance Reduction</p>
          <p className="text-xl font-bold text-white">
            <ValueDisplay value={formatCurrency(calculations.maintenanceReduction)} />
          </p>
        </div>
        
        <div className="p-5 rounded-xl bg-primary/10 border border-primary/30">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-primary uppercase tracking-wider font-semibold">First Year ROI</p>
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">
            <ValueDisplay value={formatCurrency(calculations.totalFirstYearValue)} />
          </p>
          <p className="text-sm text-white/60">
            3-year projected: <span className="text-primary font-semibold">{formatCurrency(calculations.threeYearValue)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export const ROICalculatorAdvanced = () => {
  const [activeMode, setActiveMode] = useState<CalculatorMode>('ecommerce');
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });
  
  return (
    <section 
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a] overflow-hidden"
    >
      {/* Background glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[800px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, hsl(var(--primary) / 0.04) 0%, transparent 60%)',
        }}
      />
      
      <div className="container-professional relative z-10">
        {/* Header */}
        <div 
          className="text-center mb-12 lg:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
            <Calculator className="w-4 h-4" />
            ROI Calculator
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            Calculate Your{' '}
            <span className="text-primary">Potential Savings</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            See how our services can impact your bottom line. 
            Adjust the inputs to match your business metrics.
          </p>
        </div>
        
        {/* Calculator Container */}
        <div 
          className="max-w-4xl mx-auto"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 150ms',
          }}
        >
          {/* Mode Selector */}
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            {modes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => setActiveMode(mode.id)}
                className={`flex items-center gap-3 px-5 py-3 rounded-xl border transition-all duration-300 ${
                  activeMode === mode.id
                    ? 'bg-primary/10 border-primary/40 text-white'
                    : 'bg-white/[0.02] border-white/[0.08] text-white/60 hover:border-white/20 hover:text-white'
                }`}
              >
                <span className={activeMode === mode.id ? 'text-primary' : 'text-white/40'}>
                  {mode.icon}
                </span>
                <div className="text-left">
                  <p className="text-sm font-semibold">{mode.label}</p>
                  <p className="text-xs text-white/40">{mode.description}</p>
                </div>
              </button>
            ))}
          </div>
          
          {/* Calculator Card */}
          <div 
            className="rounded-2xl border border-white/[0.08] bg-[#111111] p-6 lg:p-10"
            style={{
              boxShadow: '0 8px 40px -12px rgba(0, 0, 0, 0.5)',
            }}
          >
            {activeMode === 'ecommerce' && <EcommerceCalculator />}
            {activeMode === 'automation' && <AutomationCalculator />}
            {activeMode === 'software' && <SoftwareCalculator />}
            
            {/* Disclaimer */}
            <p className="text-xs text-white/30 text-center mt-8 pt-6 border-t border-white/[0.06]">
              * Projections are illustrative and based on industry benchmarks. Actual results may vary.
            </p>
          </div>
          
          {/* CTA */}
          <div 
            className="mt-10 text-center"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 400ms',
            }}
          >
            <a 
              href="/contact" 
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-primary hover:bg-[#00d4e6] text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
              style={{
                boxShadow: '0 4px 24px -6px hsl(var(--primary) / 0.4)',
              }}
            >
              <Sparkles className="w-5 h-5" />
              Get a Personalized Estimate
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Custom slider styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: hsl(var(--primary));
          cursor: pointer;
          border: 3px solid #0a0a0a;
          box-shadow: 0 2px 8px rgba(0, 229, 255, 0.4);
          transition: all 0.2s ease;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 16px rgba(0, 229, 255, 0.6);
        }
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: hsl(var(--primary));
          cursor: pointer;
          border: 3px solid #0a0a0a;
          box-shadow: 0 2px 8px rgba(0, 229, 255, 0.4);
        }
      `}</style>
    </section>
  );
};
